import { IS, NO, UIView, YES } from "uicore-ts"
import { RHAtomicFocusItem } from "./RHAtomicFocusItem"
import { RHNativeFocusItem } from "./RHNativeFocusItem"


/**
 * Manages sequential keyboard Tab / Shift+Tab navigation across an ordered
 * set of focusable views within one logical region of the UI (e.g. a form, a
 * toolbar, or a dialog).
 *
 * ### Relationship with RHPageFocusManager
 * `RHPageFocusManager` is the authoritative document-level Tab interceptor.
 * It collects items from all registered `RHFocusManager` instances in the active
 * layer and resolves the final navigation target itself. This class therefore no
 * longer wires per-view `TabDown` handlers for primary navigation; `rewire()`
 * only performs legacy cleanup and resets `tabIndex` values.
 *
 * ### Nested managers
 * `focusableItems` may return a mix of `UIView`, `RHFocusManager`, and
 * `RHAtomicFocusItem` instances.
 *
 * - A `UIView` with a `_focusManager` is automatically expanded in-place into
 *   the contents of its manager — the view itself controls its own Tab order
 *   without the parent needing to know about its internal structure.
 * - A `UIView` without a `_focusManager` is kept as a single Tab stop.
 * - An `RHAtomicFocusItem` (obtained via `view.atomicFocusItem()`) is always
 *   kept as a single Tab stop, suppressing auto-expansion even if the wrapped
 *   view has a `_focusManager`.
 * - A `RHFocusManager` is recursively expanded into its constituent views.
 *
 * ### Usage
 * 1. Assign `focusableItems` to a getter returning the current ordered item list.
 * 2. Register with `RHPageFocusManager` in `viewWillAppear`, deregister in `viewWillDisappear`.
 */
export class RHFocusManager {
    
    /**
     * Returns the ordered list of focusable items at the moment Tab is pressed.
     *
     * Entries may be:
     * - `UIView` — auto-expanded if the view has a `_focusManager`, otherwise
     *   kept as a single Tab stop.
     * - `RHFocusManager` — recursively expanded into its constituent views.
     * - `RHAtomicFocusItem` — always kept as a single Tab stop; suppresses
     *   auto-expansion of the wrapped view's `_focusManager`.
     *
     * Called fresh on every navigation event so structural changes are always reflected.
     */
    focusableItems: () => (UIView | RHFocusManager | RHAtomicFocusItem | RHNativeFocusItem)[] = () => []
    
    
    constructor(focusableItems: () => (UIView | RHFocusManager | RHAtomicFocusItem | RHNativeFocusItem)[]) {
        this.focusableItems = focusableItems
    }
    
    
    /**
     * Recursively expands a mixed list of `UIView`, `RHFocusManager`, and
     * `RHAtomicFocusItem` entries into a flat `UIView[]`:
     *
     * - `RHAtomicFocusItem` → push the wrapped view directly (no expansion).
     * - `RHFocusManager` → recursively expand via `_activeItems()`.
     * - `UIView` with `_focusManager` → auto-expand via the view's manager.
     * - `UIView` without `_focusManager` → push directly as a single stop.
     */
    static _expandItems(items: (UIView | RHFocusManager | RHAtomicFocusItem | RHNativeFocusItem)[]): (UIView | RHNativeFocusItem)[] {
        const result: (UIView | RHNativeFocusItem)[] = []
        for (const item of items) {
            if (item instanceof RHNativeFocusItem) {
                result.push(item)
            }
            else if (item instanceof RHAtomicFocusItem) {
                result.push(item.view)
            }
            else if (item instanceof RHFocusManager) {
                result.push(...RHFocusManager._expandItems(item._activeItems()))
            }
            else if (item._focusManager) {
                result.push(...RHFocusManager._expandItems(item._focusManager._activeItems()))
            }
            else {
                result.push(item)
            }
        }
        return result
    }
    
    
    /**
     * Moves focus to the first enabled, visible item. Called by `RHPageFocusManager`
     * when Tab enters from a preceding manager.
     */
    focusFirst() {
        const element = this._activeItems().firstElement
        if (element) {
            element.focus()
            return YES
        }
        return NO
    }
    
    
    /**
     * Moves focus to the last enabled, visible item. Called by `RHPageFocusManager`
     * when Shift+Tab enters from a following manager.
     */
    focusLast() {
        const element = this._activeItems().lastElement
        if (element) {
            element.focus()
            return YES
        }
        return NO
    }
    
    
    /** Returns focusable items that are currently in the view tree and enabled and not hidden. */
    _activeItems(): (UIView | RHNativeFocusItem)[] {
        return RHFocusManager._expandItems(this.focusableItems()).filter(
            item =>
                item.superview &&
                item.enabled &&
                IS(item.isMemberOfViewTree) &&
                item.withAllSuperviews.noneMatch(view => view.hidden)
        )
    }
    
}
